import api from './api';

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
}

export interface Company {
  _id: string;
  name: string;
  type: 'SERVICE_PROVIDER' | 'CLIENT';
  isOwner?: boolean;
  parentCompany?: string;
  email: string;
  phone?: string;
  address?: Address;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  logo?: string;
  icon?: string;
  branding?: {
    primaryColor?: string;
    sidebarColor?: string;
    sidebarTextColor?: string;
    buttonTextColor?: string;
  };
  billing?: {
    issuer?: string;
    contactName?: string;
    hourlyRate?: number;
    currency?: string;
    taxId?: string;
    swiftCode?: string;
    iban?: string;
    cardNumber?: string;
    bankName?: string;
    correspondentBank1?: string;
    correspondentBank2?: string;
    correspondentBank3?: string;
    correspondentBank4?: string;
    correspondentBank5?: string;
    correspondentCountry1?: string;
    correspondentCountry2?: string;
    correspondentCountry3?: string;
    correspondentCountry4?: string;
    correspondentCountry5?: string;
    correspondentSwift1?: string;
    correspondentSwift2?: string;
    correspondentSwift3?: string;
    correspondentSwift4?: string;
    correspondentSwift5?: string;
    paymentInstructions?: string;
  };
  modules?: {
    projects?: boolean;
    projectsHidden?: boolean;
    projectsTrialStartedAt?: string;
    projectsTrialEndsAt?: string;
  };
  users?: any[];
  createdAt: string;
  updatedAt: string;
}

export interface CompanyStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  ticketCount: number;
}

export const companyService = {
  async getCompanies(): Promise<Company[]> {
    const response = await api.get('/companies');
    return response.data.data;
  },

  async getCompany(id: string): Promise<Company> {
    const response = await api.get(`/companies/${id}`);
    return response.data.data;
  },

  async createCompany(data: Partial<Company>): Promise<Company> {
    const response = await api.post('/companies', data);
    return response.data.data;
  },

  async updateCompany(id: string, data: Partial<Company>): Promise<Company> {
    const response = await api.put(`/companies/${id}`, data);
    return response.data.data;
  },

  async deleteCompany(id: string, deleteTickets = false): Promise<void> {
    await api.delete(`/companies/${id}`, { data: { deleteTickets } });
  },

  async getCompanyStats(id: string): Promise<CompanyStats> {
    const response = await api.get(`/companies/${id}/stats`);
    return response.data.data;
  },

  async uploadLogo(id: string, image: string): Promise<string> {
    const response = await api.put(`/companies/${id}/logo`, { image });
    return response.data.data.logo;
  },

  async removeLogo(id: string): Promise<void> {
    await api.delete(`/companies/${id}/logo`);
  },

  async uploadIcon(id: string, image: string): Promise<string> {
    const response = await api.put(`/companies/${id}/icon`, { image });
    return response.data.data.icon;
  },

  async removeIcon(id: string): Promise<void> {
    await api.delete(`/companies/${id}/icon`);
  },

  async exportTickets(id: string): Promise<any> {
    const response = await api.get(`/companies/${id}/tickets/export`);
    return response.data.data;
  },

  async importTickets(id: string, payload: any): Promise<{ imported: number; skipped: number; messagesImported: number }> {
    const response = await api.post(`/companies/${id}/tickets/import`, payload);
    return response.data.data;
  },

  async getProjectDataStats(id: string): Promise<{ projectCount: number; taskCount: number; fileCount: number }> {
    const response = await api.get(`/companies/${id}/projects/stats`);
    return response.data.data;
  },

  async exportProjects(id: string): Promise<any> {
    const response = await api.get(`/companies/${id}/projects/export`);
    return response.data.data;
  },

  async importProjects(id: string, payload: any): Promise<{ imported: number; skipped: number; tasksImported: number; filesImported: number }> {
    const response = await api.post(`/companies/${id}/projects/import`, payload);
    return response.data.data;
  },

  async deleteProjects(id: string, disableModule = true): Promise<{ projectCount: number; taskCount: number; fileCount: number }> {
    const response = await api.delete(`/companies/${id}/projects`, { data: { disableModule } });
    return response.data.data;
  }
};
